<template>
	<v-app theme="light" style="background-color: #eeeeee">
		<VMain>
			<v-container fluid class="pt-6 pb-10">
				<v-row justify="center">
					<v-col cols="12" md="9" lg="7">
						<v-card
							elevation="0"
							class="audit-card"
							style="border-radius:14px;overflow:hidden;border:1px solid #e0e0e0;"
						>
							<!-- Header -->
							<div class="audit-header d-flex align-start justify-space-between px-8 py-7">
								<div>
									<span class="audit-eyebrow">AUDIT FORM</span>
									<h1 class="audit-title mt-1 mb-2">Sent by {{ senderName }}</h1>
									<p class="audit-subtitle mb-0">
										Please fill in the details below and submit when ready.
									</p>
									<div v-if="comment" class="audit-comment mt-3">
										<b>Reason provided for edit request by {{ senderName }}:</b><br />
										<p class="mb-0">{{ comment }}</p>
									</div>
								</div>
							</div>

							<!-- Questions -->
							<div class="px-8 py-2">
								<v-form ref="auditForm" v-model="formValid">
									<template v-for="(question, index) in questions">
										<div
											v-if="shouldDisplay(index)"
											:key="question.id != null ? question.id : index"
											class="py-7"
										>
											<!-- Spacer -->
											<div v-if="question.type === 'spacer'" class="my-6"></div>

											<!-- Title -->
											<div
												v-else-if="question.type === 'title'"
												class="question-title-block px-6 py-5 mb-2"
											>
												<h2 class="mb-0">{{ question.name }}</h2>
												<v-btn
													v-if="!!question.hint"
													class="mt-2"
													dark
													text
													small
													@click.stop="toggleHint(index)"
												>
													{{ !!question.hintVisible ? "Hide" : "Show" }}
													Question Details
													<v-icon right small>far fa-question-circle</v-icon>
												</v-btn>
												<v-card
													v-if="!!question.hintVisible"
													outlined
													class="my-3 white--text"
													color="transparent"
												>
													<v-card-text style="white-space: pre-wrap">{{
														question.hint
													}}</v-card-text>
												</v-card>
												<div class="text-right question-id-label mt-2" v-if="index > 0">
													(Question ID {{ index }})
												</div>
											</div>

											<!-- Attachment (auditor-provided file to download) -->
											<div
												v-else-if="question.type === 'attachment'"
												class="question-title-block px-6 py-5 mb-2"
											>
												<h2 class="mb-2">
													<v-icon dark left>fas fa-file-download</v-icon>
													{{ question.name }}
												</h2>
												<p class="mb-2">
													<i>The Auditor has provided file(s) for you to download:</i>
												</p>
												<file-uploader
													:value="question.fileID"
													:readonly="true"
													:disabled="true"
													:dark="true"
													:name="question.name"
												/>
												<v-btn
													v-if="!!question.hint"
													class="mt-2"
													dark
													text
													small
													@click.stop="toggleHint(index)"
												>
													{{ !!question.hintVisible ? "Hide" : "Show" }}
													Question Details
													<v-icon right small>far fa-question-circle</v-icon>
												</v-btn>
												<v-card
													v-if="!!question.hintVisible"
													outlined
													class="my-3 white--text"
													color="transparent"
												>
													<v-card-text style="white-space: pre-wrap">{{
														question.hint
													}}</v-card-text>
												</v-card>
												<div class="text-right question-id-label mt-2" v-if="index > 0">
													(Question ID {{ index }})
												</div>
											</div>

											<!-- Normal answerable question -->
											<template v-else>
												<div class="d-flex align-center mb-3">
													<label class="question-label">
														{{ question.name }}
														<span v-if="question.required" class="required-asterisk">*</span>
													</label>
												</div>

												<!-- Single answer text input -->
												<v-text-field
													v-if="question.type === 'singleanswer'"
													v-model="answers[index]"
													placeholder="Enter your answer:"
													:rules="question.required ? notEmptyRules : []"
													solo
													flat
													dark
													background-color="#2b2b2b"
													hide-details="auto"
													class="audit-field"
												/>

												<!-- Multiple choice (single select) -->
												<v-select
													v-else-if="question.type === 'multiplechoice'"
													v-model="answers[index]"
													:items="question.possibilities"
													placeholder="Please Choose"
													:rules="question.required ? notEmptyRules : []"
													solo
													flat
													dark
													background-color="#2b2b2b"
													hide-details="auto"
													class="audit-field"
												/>

												<!-- Multiple answers (multi select w/ checkboxes) -->
												<v-select
													v-else-if="question.type === 'multipleanswers'"
													v-model="answers[index]"
													:items="question.possibilities"
													multiple
													placeholder="Please Choose"
													:rules="question.required ? notEmptyRules : []"
													solo
													flat
													dark
													background-color="#2b2b2b"
													hide-details="auto"
													class="audit-field"
												>
													<template v-slot:item="{ active, item, attrs, on }">
														<v-list-item v-on="on" v-bind="attrs" #default="{ active }">
															<v-checkbox
																color="primary"
																class="mr-3"
																:input-value="active"
															/>{{ item }}
														</v-list-item>
													</template>
												</v-select>

												<!-- File upload -->
												<file-uploader
													v-else-if="question.type === 'file'"
													v-model="answers[index]"
													:rules="question.required ? notEmptyRules : []"
												/>

												<!-- Hint toggle -->
												<v-btn
													v-if="!!question.hint"
													class="mt-2"
													text
													small
													@click.stop="toggleHint(index)"
												>
													{{ !!question.hintVisible ? "Hide" : "Show" }}
													Question Details
													<v-icon right small>far fa-question-circle</v-icon>
												</v-btn>

												<v-card v-if="!!question.hintVisible" outlined class="my-3">
													<v-card-text
														v-if="question.type === 'policyLink'"
														style="white-space: pre-wrap"
													>
														<span v-html="formatPolicyLinkHint(question.hint)"></span>
													</v-card-text>
													<v-card-text v-else style="white-space: pre-wrap">{{
														question.hint
													}}</v-card-text>
												</v-card>

												<div class="d-flex justify-end mt-2" v-if="index > 0">
													<span class="question-id-label">(Question ID {{ index }})</span>
												</div>
											</template>
										</div>

										<v-divider
											v-if="shouldDisplay(index) && index < questions.length - 1"
											:key="`div-${question.id != null ? question.id : index}`"
										/>
									</template>
								</v-form>

								<div v-if="!questions.length" class="text-center py-10" style="color:#888;">
									No questions have been added to this form yet.
								</div>
							</div>

							<!-- Footer actions -->
							<div class="audit-footer d-flex align-center justify-end px-8 py-5">
								<!-- <v-btn
									outlined
									color="#0b7078"
									style="border-radius:8px;"
									@click="saveDraft"
								>
									<v-icon left size="18">fas fa-save</v-icon>
									Save as draft
								</v-btn> -->

								<v-btn
									depressed
									:color="isFormComplete ? '#0b7078' : undefined"
									:dark="isFormComplete"
									:disabled="!isFormComplete"
									class="submit-audit-btn"
									style="border-radius:8px;"
									@click="submitForm"
								>
									<v-icon left size="18" :color="isFormComplete ? '#fff' : undefined">
										fas fa-paper-plane
									</v-icon>
									Submit and send
								</v-btn>
							</div>
						</v-card>
					</v-col>
				</v-row>
			</v-container>
		</VMain>

		<!-- Submit confirmation dialog -->
		<v-dialog v-model="dialogSend" max-width="600px">
			<v-card class="confirm-card">
				<v-card-text class="text-center px-8 pt-10 pb-10">
					<div class="dialog-icon-wrap mx-auto mb-6">
						<v-icon size="26" color="#0b7078">fas fa-circle-question</v-icon>
					</div>

					<h2 class="mb-3">Are you sure?</h2>
					<p class="dialog-subtitle mb-8">
						You will not be able to change your answers at a later time.
					</p>

					<div class="d-flex justify-center">
						<v-btn
							outlined
							color="error"
							class=" px-8 mr-3"
							style="border-radius:8px;"
							@click="dialogSend = false"
						>
							Cancel
						</v-btn>
						<v-btn
							depressed
							dark
							color="#0b7078"
							class="text-none px-8"
							style="border-radius:8px;"
							@click="submitAudit"
						>
							Submit audit
						</v-btn>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- Send to team confirmation dialog -->
		<!-- <v-dialog v-model="draftmsg" max-width="650px">
			<v-card class="confirm-card">
				<v-card-text class="text-center px-8 pt-10 pb-10">
					<div class="dialog-icon-wrap mx-auto mb-6">
						<v-icon size="26" color="#0b7078">fas fa-users</v-icon>
					</div>

					<h2 class="mb-3">
						Do you want to send this to your team to fill out?
					</h2>
					<p class="dialog-subtitle mb-8">
						Your draft has been saved. You can share this form with your team so they can complete it together.
					</p>

					<div class="d-flex justify-center">
						<v-btn
							outlined
							color="error"
							class=" px-8 mr-3"
							style="border-radius:8px;"
							@click="draftmsg = false"
							:to="'/guestPortal'"
						>
							No
						</v-btn>
						<v-btn
							depressed
							dark
							color="#0b7078"
							class=" px-8"
							style="border-radius:8px;"
							@click="shareTeam()"
						>
							Yes
						</v-btn>
					</div>
				</v-card-text>
			</v-card>
		</v-dialog> -->

		<!-- Share with team dialog -->
		<v-dialog v-model="draftlink" max-width="600px">
			<v-card class="share-card">
				<v-card-text class="text-center px-8 pt-10 pb-10">
					<div class="dialog-icon-wrap mx-auto mb-6">
						<v-icon size="26" color="#0b7078">fas fa-link</v-icon>
					</div>

					<h2 class="mb-3">Share with your team</h2>
					<p class="dialog-subtitle mb-7">
						Copy the link below and send it to your team members so they can fill out this form.
					</p>

					<div class="share-link-row d-flex align-stretch mb-3">
						<div class="share-link-box flex-grow-1 text-left">
							{{ inviteLink }}
						</div>
						<v-btn
							depressed
							dark
							color="#0b7078"
							class=" share-copy-btn"
							style="border-radius:8px;"
							@click="copyLink"
						>
							<v-icon left size="16">fas fa-copy</v-icon>
							{{ copyLabel }}
						</v-btn>
					</div>

					<p class="mb-8">
						Anyone with this link can fill out and submit the form.
					</p>
					<v-btn
						outlined
						color="error"
						class=" px-8 mr-3"
						style="border-radius:8px;"
						@click="draftlink = false"
						:to="'/guestPortal'"
					>
						Cancel
					</v-btn>
					<v-btn
						depressed
						dark
						color="#0b7078"
						class=" px-10"
						style="border-radius:8px;"
						@click="draftlink = false"
						:to="'/guestPortal'"
					>
						Done
					</v-btn>
				</v-card-text>
			</v-card>
		</v-dialog>
	</v-app>
</template>

<script>
import RequestAPI from "../api/audit/request"
import LoadingState from "../consts/LoadingState"
import router from "../router"
import FileUploaderVue from "../components/FileUploader.vue"
import AuditAPI from "../api/audit/audit"

export default {
	name: "GuestAudit",
	components: {
		"file-uploader": FileUploaderVue
	},
	data() {
		return {
			senderName: "",
			companyLogo: null,
			formValid: false,
			savingDraft: false,
			notEmptyRules: [v => !!v || "This field cannot be empty"],
			answers: [],
			rules: {
				required: (v) =>
					(Array.isArray(v) ? v.length > 0 : !!v) || "This field is required",
			},
			id: null,
			questions: [],
			comment: null,
			edit: false,
			auditID: null,
			fileID: null,
			dialogSend: false,
			/* draftmsg: false, */
			draftlink: false,
			inviteLink: "",
			copyLabel: "Copy link",
			auditSuccessDialog: false,
			recipientName: "",
			submission: {
				fullName: "",
				companyName: "",
				email: "",
			},
			lastSaveDate: 0,
			initializing: false,
			saveTimeoutId: null,
		}
	},
	computed: {
		initials() {
			if (!this.submission.fullName) return ""
			return this.submission.fullName.trim().charAt(0).toUpperCase()
		},
		isFormComplete() {
			if (!this.questions.length) return false

			for (let index = 0; index < this.questions.length; index++) {
				const question = this.questions[index]

				// Skip non-answerable question types and any question
				// that isn't currently visible on the form.
				if (
					question.type === "spacer" ||
					question.type === "title" ||
					question.type === "attachment"
				) {
					continue
				}
				if (!this.shouldDisplay(index)) {
					continue
				}
				if (!question.required) {
					continue
				}

				const answer = this.answers[index]
				const isEmpty = Array.isArray(answer)
					? answer.length === 0
					: answer === null || answer === undefined || answer === ""

				if (isEmpty) {
					return false
				}
			}

			return true
		},
	},
	mounted() {
		if (isNaN(parseInt(this.$route.params.id))) {
			router.push("/")
		} else {
			this.id = parseInt(this.$route.params.id)
			this.loadAudit()
		}
	},
	beforeDestroy() {
		if (this.saveTimeoutId) {
			clearTimeout(this.saveTimeoutId)
			this.saveTimeoutId = null
		}
	},
	watch: {
		answers() {
			if (this.initializing) return
			this.saveAudit()
		}
	},
	methods: {
		shouldDisplay(index) {
			if (!this.questions[index].dependencyQuestionID) {
				return true
			}
			if (!this.answers[this.questions[index].dependencyQuestionID]) {
				return false
			}
			if (
				this.questions[index].dependencyQuestionValue.includes(
					this.answers[this.questions[index].dependencyQuestionID]
				)
			) {
				return true
			}

			if (
				typeof this.answers[this.questions[index].dependencyQuestionID] ==
				"object"
			) {
				for (let element of this.answers[
					this.questions[index].dependencyQuestionID
				]) {
					if (this.questions[index].dependencyQuestionValue.includes(element)) {
						return true
					}
				}
			}
			return false
		},
		toggleHint(index) {
			let source = { hintVisible: !this.questions[index].hintVisible }
			let newQuestion = Object.assign({}, this.questions[index], source)
			this.$set(this.questions, index, newQuestion)
		},
		formatPolicyLinkHint(hint) {
			const matchText = /"([^"]+)"/
			const match = hint.match(matchText)

			if (match) {
				const linkText = match[1]
				const link = `<a href="${linkText}" target="_blank">Policy & Certificate Link</a>`
				return hint.replace(`"${linkText}"`, `"${link}"`)
			}
			return hint
		},
		/* saveDraft() {
			this.draftmsg = true
			setTimeout(() => {
				this.$root.$emit?.("toast", "Draft saved")
			}, 500)
		}, */
		registerNow() {
			this.auditSuccessDialog = false
			this.$router.push("/register")
		},
		maybeLater() {
			this.auditSuccessDialog = false
			this.$router.push("/guestPortal")
		},
		shareTeam() {/* 
			this.draftmsg = false */
			this.inviteLink = this.generateInviteLink()
			this.copyLabel = "Copy link"
			this.draftlink = true
		},
		generateInviteLink() {
			const segment = () =>
				Math.random().toString(36).substring(2, 8).toUpperCase()
			const code = `${segment()}-${segment()}-${segment()}-${segment()}`
			const origin =
				typeof window !== "undefined" ? window.location.origin : ""
			return `${origin}/invite?code=${code}`
		},
		async copyLink() {
			try {
				if (navigator.clipboard) {
					await navigator.clipboard.writeText(this.inviteLink)
				}
				this.copyLabel = "Copied!"
				this.$root.$emit?.("toast", "Link copied")
				setTimeout(() => {
					this.copyLabel = "Copy link"
				}, 5000)
			} catch (e) {
				this.$root.$emit?.("toast", "Couldn't copy link")
			}
		},
		submitForm() {
			this.$refs.auditForm.validate()
			if (!this.isFormComplete) return
			this.dialogSend = true
		},
		submitAudit() {
			this.dialogSend = false
			this.lastSaveDate = Date.now() + 10000
			AuditAPI.addAudit(this.id, this.answers, null, 0, null)
				.then((data) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully completed audit with ID '${data}'`,
						{ root: true }
					)
					this.$router.push("/guestauditsuccess")
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		saveAudit(fromTimeout) {
			fromTimeout = fromTimeout || false
			if (Date.now() - this.lastSaveDate < 2000) {
				console.log(`Tried to save too close to previous date. Diff was: ${+Date.now() - this.lastSaveDate}`)
				return
			}
			this.lastSaveDate = Date.now()
			RequestAPI.updateRequest(this.id, this.answers)
				.then(() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						`Successfully saved audit draft!`,
						{ root: true }
					)
					if (fromTimeout) {
						return
					}
					this.saveTimeoutId = setTimeout(() => {
						this.saveAudit(true)
					}, 3500)
				})
				.catch((error) => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						error,
						{ root: true }
					)
				})
		},
		loadAudit() {
			this.initializing = true

			this.$store.dispatch(
				"loading/" + LoadingState.ACTION_START_LOADING,
				null,
				{ root: true }
			)

			RequestAPI.get(this.id)
				.then((data) => {
					this.questions = data.questions
					this.senderName = data.senderName
					this.companyLogo = data.logoURL
					this.comment = data.comment
					this.edit = data.edit
					this.auditID = data.auditID
					this.fileID = data.fileID

					this.answers = []
					for (let i = 0; i < this.questions.length; i++) {
						this.answers.push(
							this.questions[i].type === "multipleanswers" ? [] : null
						)
					}

					if (data.answers) {
						this.answers = data.answers
					}

					this.$nextTick(() => {
						this.initializing = false
					})

					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_END_LOADING,
						null,
						{ root: true }
					)
				})
				.catch((err) => {
					this.initializing = false
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_SET_ERROR,
						err,
						{ root: true }
					)
				})
		},
	},
}
</script>

<style scoped>
.audit-header {
	background: #0b7078;
}
.audit-eyebrow {
	color: #9fd8da;
	letter-spacing: 0.08em;
}
.audit-title {
	color: #fff;
	line-height: 1.2;
}
.audit-subtitle {
	color: #d8eeee;
}
.company-logo-box {
	background: #fff;
	border: 1px solid #cfd8da;
	border-radius: 6px;
	padding: 10px 18px;
	min-width: 130px;
	min-height: 44px;
}

.question-label {
	color: #1a1a1a;
}
.required-asterisk {
	color: #d62b2b;
	margin-left: 2px;
}
.question-id-label {
	color: #9a9a9a;
}

.audit-footer {
	background: #f4f6f6;
	border-top: 1px solid #e3e3e3;
}

/* Active/enabled state: solid teal button */
.submit-audit-btn:not(.v-btn--disabled) {
	background-color: #0b7078 !important;
	color: #fff !important;
}
.submit-audit-btn:not(.v-btn--disabled) :deep(.v-icon) {
	color: #fff !important;
}

/* Disabled state: greyed-out, matches the "Submit Audit" reference look
   until every required question has been answered. */
.submit-audit-btn.v-btn--disabled,
.submit-audit-btn.v-btn.v-btn--disabled {
	background-color: #e0e0e0 !important;
	color: #a9a9a9 !important;
	opacity: 1 !important;
	box-shadow: none !important;
	cursor: not-allowed;
}
.submit-audit-btn.v-btn--disabled :deep(.v-icon),
.submit-audit-btn.v-btn.v-btn--disabled :deep(.v-icon) {
	color: #a9a9a9 !important;
}

.audit-field :deep(input),
.audit-field :deep(textarea) {
	color: #fff !important;
}
.audit-field :deep(.v-input__slot) {
	border-radius: 8px;
}

/* Shared dialog header styles (icon badge, title, subtitle) */
.share-card,
.confirm-card {
	border-radius: 14px;
}
.dialog-icon-wrap {
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: #e1f3f1;
	display: flex;
	align-items: center;
	justify-content: center;
}
.dialog-subtitle {
	max-width: 440px;
	margin: 0 auto;
	line-height: 1.5;
}
.share-link-row {
	gap: 12px;
}
.share-link-box {
	background: #eef2f2;
	border: 1px solid #dde5e5;
	border-radius: 8px;
	padding: 14px 16px;
	color: #0b7078;
	line-height: 1.4;
	word-break: break-all;
	display: flex;
	align-items: center;
}
.share-copy-btn {
	flex-shrink: 0;
	white-space: nowrap;
	width: 150px;
	justify-content: center;
}

@media (max-width: 600px) {
	.audit-header {
		flex-direction: column;
	}
	.company-logo-box {
		margin-left: 0 !important;
		margin-top: 16px;
	}
	.audit-footer {
		flex-direction: column;
		gap: 12px;
	}
	.audit-footer .v-btn {
		width: 100%;
	}
	.share-link-row {
		flex-direction: column;
	}
	.share-copy-btn {
		width: 100%;
	}
}
</style>